import React, { useMemo } from "react";

import { Box, Stack } from "@mui/material";
import { Typography } from "components";
import { gray } from "configs/colors";
import { rem } from "utils";
import { getPrimaryColor } from "utils/theme";

import ChipStatus from "./ChipStatus";

interface CardInfoProps {
  title: string;
  description: string;
  firstLabel?: string;
  firstValue?: string;
  secondLabel?: string;
  secondValue?: string;
  thirdLabel?: string;
  thirdValue?: string;
  status: number;
  isActive: boolean;
  menu?: React.ReactNode;
}

const CardInfo: React.FC<CardInfoProps> = ({
  title,
  description,
  firstValue,
  firstLabel,
  secondLabel,
  secondValue,
  thirdLabel,
  thirdValue,
  status,
  isActive,
  menu,
}) => {
  const borderStyle = useMemo(
    () =>
      isActive
        ? `2px solid ${getPrimaryColor()[600]}`
        : `1px solid ${gray[300]}`,
    [isActive]
  );
  return (
    <Box
      border={borderStyle}
      borderRadius="12px"
      overflow="hidden"
      className="fade-in-fwd"
      height="100%"
    >
      <Stack
        p={`${rem(16)} ${rem(16)}`}
        paddingRight={rem(4)}
        direction="row"
        borderBottom={borderStyle}
        bgcolor={gray[150]}
      >
        <Stack flex="auto">
          <Typography
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              "-webkit-line-clamp": "2",
              "-webkit-box-orient": "vertical",
            }}
            fontWeight={600}
            type="TextMD"
            color="primary.800"
          >
            {title}
          </Typography>
        </Stack>
        <Stack
          minWidth="123px"
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          columnGap={rem(8)}
        >
          <ChipStatus status={status} />
          {menu}
        </Stack>
      </Stack>
      <Stack
        p={rem(16)}
        rowGap={rem(12)}
        height="fit-content"
        justifyContent="space-between"
      >
        <Typography
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            "-webkit-line-clamp": "2",
            "-webkit-box-orient": "vertical",
          }}
          fontWeight={400}
          type="TextSM"
          color="grey.600"
        >
          {description}
        </Typography>
        <Stack direction="row" flexWrap="wrap">
          {firstLabel && (
            <Stack flexGrow="1">
              <Typography fontWeight={400} type="TextSM" color="grey.600">
                {firstLabel} :
              </Typography>
              <Typography fontWeight={600} type="TextMD" color="grey.700">
                {firstValue}
              </Typography>
            </Stack>
          )}
          {secondLabel && (
            <Stack flexGrow="1">
              <Typography fontWeight={400} type="TextSM" color="grey.600">
                {secondLabel} :
              </Typography>
              <Typography fontWeight={600} type="TextMD" color="grey.700">
                {secondValue}
              </Typography>
            </Stack>
          )}
        </Stack>
        {thirdLabel && (
          <Stack>
            <Typography fontWeight={400} type="TextSM" color="grey.600">
              {thirdLabel} :
            </Typography>
            <Typography fontWeight={600} type="TextMD" color="grey.700">
              {thirdValue}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default CardInfo;
